import { ThemeEvents } from "@theme/events";
import { Component } from "@theme/component";
import { observerElement, onIdleAfterPageLoad } from "@theme/utilities";

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = null;

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver = observerElement({
      element: this,
      callback: () => this.loadRecommendations(),
      threshold: '1000'
    });
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  loadRecommendations() {
    const { recommendationsPerformed, productId, sectionId } = this.dataset;
    const productListRecommendations = this.querySelector('.product-list-recommendations');
    if (!productListRecommendations) {
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    const { url, intent } = productListRecommendations.dataset;
    const id = this.id;
    if (!productId || !id) {
      return;
    }

    this.#fetchCachedRecommendations(url, productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`.product-recommendations[id="${id}"]`);
        const recommendationsList = recommendations?.querySelector('.product-list-recommendations');
        if (recommendationsList?.innerHTML && recommendationsList.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.classList.remove('hidden');
          if (this.#recommendedProductIds(this) !== this.#recommendedProductIds(recommendations)) {
            this.innerHTML = recommendations.innerHTML;
          }
        } else {
          this.classList.add('hidden');
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Lists the products a recommendations container currently renders, in order.
   * Rewriting the container with an identical list would rebuild the cards and make
   * the standard events runtime report a second view for products already seen.
   * @param {Element} container
   * @returns {string}
   */
  #recommendedProductIds(container) {
    return Array.from(container.querySelectorAll('product-card, product-card-compact'))
      .map((card) => card.getAttribute('data-product-id'))
      .join(',');
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} product_recommendations_url
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(product_recommendations_url, productId, sectionId, intent) {
    const url = `${product_recommendations_url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      if (!Shopify.designMode) {
        this.#cachedRecommendations[url] = text;
      }
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    this.classList.add('hidden');
    this.dataset.recommendationsPerformed = 'true';
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

class CartDrawerProductRecommendations extends ProductRecommendations {
  connectedCallback() {
    super.connectedCallback();
    onIdleAfterPageLoad(() => this.loadRecommendations());
    this.boundLoadCartRecommendations = this.loadCartRecommendations.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.boundLoadCartRecommendations);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.boundLoadCartRecommendations) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.boundLoadCartRecommendations);
    }
  }

  /**
   * Reloads recommendations when cart updates
   * Resets performed flag — mutation observer triggers loadRecommendations automatically
   */
  loadCartRecommendations() {
    this.classList.add('hidden');
    this.dataset.recommendationsPerformed = 'false';
    // Mutation observer picks up the attribute change and calls loadRecommendations
  }
}

if (!customElements.get('cart-drawer-product-recommendations')) {
  customElements.define('cart-drawer-product-recommendations', CartDrawerProductRecommendations);
}